import { useState, useCallback, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  FormFeedback,
} from "reactstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  ExternalLink,
  Download,
  FileText,
  Image,
  Video,
  Music,
  File,
  Archive,
  BarChart3,
  Presentation,
} from "lucide-react";

import { apiSlice } from "@/app/apiSlice";
import { showToast } from "components/Toasts/helpers/showToast";
import { getChangedValues } from "helpers/getChangedValues";
import {
  CreatorLinkCellRenderer,
  CampaignLinkCellRenderer,
} from "components/DataGrid/components";

interface EditDocumentModalProps {
  documentId: string;
  isOpen: boolean;
  onClose: () => void;
  onDocumentSaved?: () => void;
}

const validationSchema = Yup.object({
  title: Yup.string().optional(),
});

export const EditDocumentModal = ({
  documentId,
  isOpen,
  onClose,
  onDocumentSaved,
}: EditDocumentModalProps) => {
  const [updateDocument, { isLoading: isUpdating }] =
    apiSlice.useUpdateDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] =
    apiSlice.useDeleteDocumentMutation();

  const {
    data: document,
    isLoading: isLoadingDocument,
    error,
  } = apiSlice.useGetDocumentQuery(documentId, { skip: !documentId });

  const doc = document?.result || document;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDelete = useCallback(async () => {
    if (!documentId) return;

    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await deleteDocument(documentId).unwrap();
      showToast({ type: "success", message: "Document deleted successfully" });
      onClose();
    } catch (error) {
      console.error("Error deleting document -", error);
      showToast({ type: "error", message: "Error deleting document" });
    }
  }, [documentId, deleteDocument, onClose]);

  // Helper function to fetch document blob
  const fetchDocumentBlob = useCallback(async () => {
    const url = `${import.meta.env.VITE_API_URL}/api/v1/document/${documentId}/download`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) throw new Error("Error fetching document");
    return await response.blob();
  }, [documentId]);

  // Helper function to get filename with extension
  const getFilename = useCallback(
    (response: Response) => {
      const contentDisposition = response.headers.get("content-disposition");
      let filename = doc?.title || "document";

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Add file extension if not present
      if (doc?.mimetype && !filename.includes(".")) {
        const extension = doc.mimetype.split("/")[1];
        if (extension) filename += `.${extension}`;
      }

      return filename;
    },
    [doc?.title, doc?.mimetype],
  );

  const handleDownload = useCallback(async () => {
    if (!documentId) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/document/${documentId}/download`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!response.ok) throw new Error("Error downloading document");

      const blob = await response.blob();
      const filename = getFilename(response);
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = window.document.createElement("a");
      a.href = downloadUrl;
      a.download = filename;
      a.style.display = "none";
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      showToast({ type: "success", message: "Download started" });
    } catch (error) {
      console.error("Error downloading document -", error);
      showToast({ type: "error", message: "Error downloading document" });
    }
  }, [documentId, getFilename]);

  const handleView = useCallback(async () => {
    if (!documentId) return;

    try {
      const blob = await fetchDocumentBlob();
      const viewUrl = window.URL.createObjectURL(blob);
      window.open(viewUrl, "_blank", "noopener, noreferrer");
    } catch (error) {
      console.error("Error viewing document -", error);
      showToast({ type: "error", message: "Error viewing document" });
    }
  }, [documentId, fetchDocumentBlob]);

  const handleSubmit = async (values: any, actions: any) => {
    try {
      const initialValues = {
        files: doc?.files || [],
        title: doc?.title || "",
        campaign_id: doc?.campaign_id || "",
        user_id: doc?.user_id || "",
      };

      // Only send changed values
      const changedValues = getChangedValues(values, initialValues);

      // If no values have changed, show a message and return
      if (Object.keys(changedValues).length === 0) {
        showToast({
          type: "info",
          message: "No changes detected",
        });
        actions.setSubmitting(false);
        return;
      }

      const { result, error } = await updateDocument({
        document_id: documentId,
        ...changedValues,
      }).unwrap();
      if (error) throw new Error(error);
      showToast({
        type: "success",
        message: "Document updated successfully",
      });

      actions.setSubmitting(false);
      onDocumentSaved?.();
      onClose();
    } catch (error) {
      console.error("Error updating document -", error);
      showToast({
        type: "error",
        message: "Error updating document",
      });
      actions.setSubmitting(false);
    }
  };

  const initialValues = useMemo(() => {
    return {
      files: doc?.files || [],
      title: doc?.title || "",
      campaign_id: doc?.campaign_id || "",
      user_id: doc?.user_id || "",
    };
  }, [doc]);

  if (isLoadingDocument) {
    return (
      <Modal isOpen={isOpen} toggle={handleClose} size="lg" backdrop="static">
        <ModalHeader toggle={handleClose}>Loading Document...</ModalHeader>
        <ModalBody>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={isOpen}
        toggle={handleClose}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader toggle={handleClose}>Error</ModalHeader>
        <ModalBody>
          <div className="alert alert-danger">
            Error loading document. Please try again.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  const isLoading = isUpdating || isDeleting;

  // Helper function to get file type icon and display name
  const getFileTypeInfo = (mimetype: string) => {
    if (!mimetype) return { Icon: FileText, type: "Unknown" };

    if (mimetype.startsWith("image/")) return { Icon: Image, type: "Image" };
    if (mimetype.startsWith("video/")) return { Icon: Video, type: "Video" };
    if (mimetype.startsWith("audio/")) return { Icon: Music, type: "Audio" };
    if (mimetype.includes("pdf")) return { Icon: FileText, type: "PDF" };
    if (mimetype.includes("word") || mimetype.includes("document"))
      return { Icon: FileText, type: "Document" };
    if (mimetype.includes("spreadsheet") || mimetype.includes("excel"))
      return { Icon: BarChart3, type: "Spreadsheet" };
    if (mimetype.includes("presentation") || mimetype.includes("powerpoint"))
      return { Icon: Presentation, type: "Presentation" };
    if (mimetype.includes("zip") || mimetype.includes("archive"))
      return { Icon: Archive, type: "Archive" };

    return {
      Icon: File,
      type: mimetype.split("/")[1]?.toUpperCase() || "File",
    };
  };

  const fileTypeInfo = getFileTypeInfo(doc?.mimetype || "");
  const { Icon: FileIcon } = fileTypeInfo;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader toggle={handleClose}>Edit Document</ModalHeader>
      <Formik
        key={doc?._id}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          validateForm,
          errors,
          touched,
          submitForm,
          isSubmitting,
        }) => (
          <>
            <ModalBody>
              <Form>
                <Row>
                  {/* File Info Section */}
                  <Col md={12}>
                    <div className="border rounded p-3 mb-3 bg-light">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <FileIcon className="me-3" size={32} />
                          <div>
                            <h6 className="mb-1">
                              {doc?.title || "Untitled Document"}
                            </h6>
                            <small className="text-muted">
                              {fileTypeInfo.type} •{" "}
                              {doc?.mimetype || "Unknown type"}
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            color="outline-primary"
                            size="sm"
                            onClick={handleView}
                            disabled={isLoading}
                          >
                            <ExternalLink size={16} className="me-1" />
                            View
                          </Button>
                          <Button
                            color="outline-primary"
                            size="sm"
                            onClick={handleDownload}
                            disabled={isLoading}
                          >
                            <Download size={16} className="me-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* Title Field */}
                  <Col md={12}>
                    <FormGroup>
                      <Label htmlFor="title">Title</Label>
                      <Input
                        type="text"
                        name="title"
                        id="title"
                        value={values.title}
                        onChange={(e) => {
                          setFieldValue("title", e.target.value);
                          setFieldTouched("title", true);
                        }}
                        onBlur={() => setFieldTouched("title", true)}
                        placeholder="Enter title"
                        invalid={touched.title && !!errors.title}
                      />
                      <ErrorMessage name="title" component={FormFeedback} />
                    </FormGroup>
                  </Col>

                  {/* Campaign Field - only show if has value */}
                  {values.campaign_id && (
                    <Col md={6}>
                      <FormGroup>
                        <Label htmlFor="campaign_id">Campaign</Label>
                        <div className="form-control-plaintext">
                          <CampaignLinkCellRenderer
                            value={
                              values.campaign_id?._id || values.campaign_id
                            }
                            data={{
                              name: doc?.campaign_id?.name || doc?.campaignName,
                            }}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                  )}

                  {/* Creator Field - only show if has value */}
                  {values.user_id && (
                    <Col md={6}>
                      <FormGroup>
                        <Label htmlFor="user_id">Creator</Label>
                        <div className="form-control-plaintext">
                          <CreatorLinkCellRenderer
                            value={values.user_id?._id || values.user_id}
                            data={{
                              name: doc?.user_id?.name,
                            }}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                  )}
                </Row>
              </Form>
            </ModalBody>

            <ModalFooter>
              <Button
                color="danger"
                onClick={handleDelete}
                disabled={isLoading}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
              <Button color="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={submitForm}
                disabled={isSubmitting || isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </Modal>
  );
};
